import React from "react";
import { IVaccination } from "@/types/vaccination";
import { ListItem, ListItemAction } from "@/components/ui/atoms/MListItem";
import {
  IconEdit,
  IconCircleX,
  IconCheckbox,
  IconBell,
} from "@tabler/icons-react";
import { t } from "i18next";
import {
  formatPrettyDate,
  formatReminderDateWithTimezone,
} from "@/utils/date-utils";
import "./vaccine-tracker.css";

interface VaccinationCardProps {
  vaccination: IVaccination;
  onEdit: (vaccination: IVaccination) => void;
  onMarkAsDone: (vaccination: IVaccination) => void;
  onDelete: (vaccination: IVaccination) => void;
}

export const VaccinationCard: React.FC<VaccinationCardProps> = ({
  vaccination,
  onEdit,
  onMarkAsDone,
  onDelete,
}) => {
  // Generate emphasized text (due date) for upcoming vaccinations
  const getEmphasizedText = () => {
    if (!vaccination.taken && vaccination.dueDate) {
      const date = new Date(vaccination.dueDate);
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "short" });
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

      return `Due on ${day}-${month} at ${formattedHours}:${formattedMinutes}${ampm}`;
    }
    return "";
  };

  // Generate secondary text based on vaccination status
  const getSecondaryText = () => {
    if (vaccination.taken) {
      return vaccination.takenOn
        ? t("vaccinations.list.takenOn", {
            date: formatPrettyDate(vaccination.takenOn),
          })
        : t("vaccinations.list.takenOnNotSpecified");
    }
    return "";
  };

  // Get tertiary text (reminder notification text for upcoming, empty for taken)
  const getTertiaryText = () => {
    if (vaccination.taken) {
      return "";
    }

    const days = vaccination.reminderDays;

    if (days === null || days === undefined) {
      return "No reminder set";
    }

    if (days === 0) {
      return "On the day";
    }

    if (days === 1) {
      return "1 day before";
    }

    return `${days} days before`;
  };

  // Create actions for the ListItem
  const getVaccinationActions = (): ListItemAction[] => {
    const actions: ListItemAction[] = [];
    // Define actions based on vaccination state
    if (!vaccination.taken) {
      actions.push({
        id: "mark-as-done",
        label: t("vaccinations.list.markAsDone"),
        icon: <IconCheckbox size={20} />,
        onClick: () => onMarkAsDone(vaccination),
        isVisible: true,
      });
    }

    actions.push(
      {
        id: "edit",
        label: t("common.edit"),
        icon: <IconEdit size={20} />,
        onClick: () => onEdit(vaccination),
        isVisible: true,
      },
      {
        id: "delete",
        label: t("common.delete"),
        icon: <IconCircleX size={20} />,
        onClick: () => onDelete(vaccination),
        className: "text-semantic-icon-error",
        isVisible: true,
      }
    );
    return actions;
  };

  // For upcoming vaccinations, use the reminders listing style
  if (!vaccination.taken) {
    return (
      <div className="mb-4 relative">
        <ListItem
          usage="crud"
          type="3-line"
          variant="purple"
          title={vaccination.name}
          emphasizedText={getEmphasizedText()}
          tertiaryText={getTertiaryText()}
          tertiaryIcon={<IconBell size={16} />}
          tertiaryVariant="orange"
          actions={getVaccinationActions()}
        />
      </div>
    );
  }

  // For taken vaccinations, keep the original style
  return (
    <div className="mb-4 relative">
      <ListItem
        usage="crud"
        type="3-line"
        state={"default"}
        variant="purple"
        title={vaccination.name}
        secondaryText={getSecondaryText()}
        actions={getVaccinationActions()}
      />
    </div>
  );
};

export default VaccinationCard;
